import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { generateReview } from "../services/review.service";
import type { GenerateReviewRequest } from "../types/review.types";

export const useGenerateReview = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ request, signal }: { request: GenerateReviewRequest; signal: AbortSignal }) => {
      return generateReview(request, signal);
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        ["reviews", variables.request.document_id, [variables.request.query_term]],
        data
      );
      void queryClient.invalidateQueries({ queryKey: ["reviews"] });
    },
    onError: (error, variables) => {
      // A cancelled review is a deliberate user action, not a failure.
      if (error.name === "CanceledError") {
        console.log("Review generation canceled for:", variables.request.query_term);
        return;
      }

      console.error(
        "Hook: Error generating review for:",
        variables.request.query_term,
        "Error:",
        error
      );

      // Without this the request simply vanishes — the spinner stops and
      // nothing else happens, which reads to the user as the add-in losing
      // their work (QS-113). Every other mutation in the app reports failure.
      const term = variables.request.query_term.replace(/-/g, " ");
      const timedOut = "code" in error && error.code === "ECONNABORTED";

      toast.error("Review Failed", {
        description: timedOut
          ? `The "${term}" review took too long to respond. Please try again.`
          : `Could not generate the "${term}" review. Please try again.`,
      });
    },
  });
};
